import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './MonitoringWidget.css';

interface MonitoringWidgetProps {
  onRescan: (title: string, year?: string, type?: string, priority?: string) => void;
}

export default function MonitoringWidget({ onRescan }: MonitoringWidgetProps) {
  const monitored = useQuery(api.monitoring.getMonitored);
  const needingRescan = useQuery(api.monitoring.getNeedingRescan);
  const toggleMonitoring = useMutation(api.monitoring.toggleMonitoring);
  const removeFromMonitoring = useMutation(api.monitoring.removeFromMonitoring);

  if (!monitored) {
    return null;
  }

  const handleToggle = async (id: any, active: boolean) => {
    await toggleMonitoring({ monitoringId: id, active: !active });
  };

  const handleRemove = async (id: any) => {
    if (confirm('Remove this title from monitoring?')) {
      await removeFromMonitoring({ monitoringId: id });
    }
  };

  const handleRescan = (item: any) => {
    onRescan(item.contentTitle, item.contentYear, item.contentType, item.priority);
  };

  const getDaysSinceLastScan = (lastScanned: number | null) => {
    if (!lastScanned) return null;
    const days = Math.floor((Date.now() - lastScanned) / (1000 * 60 * 60 * 24));
    return days;
  };

  const needsRescanIds = new Set((needingRescan || []).map((item: any) => item._id));

  return (
    <div className="monitoring-widget card">
      <div className="widget-header" style={{ justifyContent: 'flex-end' }}>
        <span className="monitored-count">{monitored.length} titles</span>
      </div>

      {monitored.length === 0 ? (
        <div className="empty-monitoring">
          <p>No titles being monitored yet</p>
          <p className="hint">Click "Add to Monitoring" from scan history to track titles</p>
        </div>
      ) : (
        <>
          {needingRescan && needingRescan.length > 0 && (
            <div className="needs-rescan-alert">
              ⚠️ {needingRescan.length} title{needingRescan.length !== 1 ? 's' : ''} need
              {needingRescan.length === 1 ? 's' : ''} rescanning
            </div>
          )}

          <div className="monitoring-list">
            {monitored.map((item: any) => {
              const daysSince = getDaysSinceLastScan(item.lastScanned);
              const needsRescan = needsRescanIds.has(item._id);

              return (
                <div
                  key={item._id}
                  className={`monitoring-item ${!item.active ? 'inactive' : ''} ${needsRescan ? 'needs-rescan' : ''}`}
                >
                  <div className="item-info">
                    <div className="item-title">{item.contentTitle}</div>
                    <div className="item-meta">
                      {item.contentYear && <span>{item.contentYear}</span>}
                      {item.contentType && <span>{item.contentType}</span>}
                      <span className={`frequency-badge ${item.scanFrequency}`}>
                        {item.scanFrequency}
                      </span>
                    </div>
                    {daysSince !== null && (
                      <div className="last-scan">
                        Last scanned: {daysSince === 0 ? 'today' : `${daysSince}d ago`}
                      </div>
                    )}
                    {!item.lastScanned && (
                      <div className="last-scan never">Never scanned</div>
                    )}
                  </div>

                  <div className="item-actions">
                    {needsRescan && (
                      <button
                        onClick={() => handleRescan(item)}
                        className="btn-rescan-now"
                        title="This title is due for rescanning"
                      >
                        🔄 Rescan Now
                      </button>
                    )}

                    <button
                      onClick={() => handleToggle(item._id, item.active)}
                      className={`btn-toggle ${item.active ? 'active' : 'paused'}`}
                      title={item.active ? 'Pause monitoring' : 'Resume monitoring'}
                    >
                      {item.active ? '⏸' : '▶️'}
                    </button>

                    <button
                      onClick={() => handleRemove(item._id)}
                      className="btn-remove"
                      title="Remove from monitoring"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
